export type SupportedPlatform = 'youtube' | 'vimeo' | 'facebook' | 'tiktok' | 'instagram' | 'twitch' | 'dailymotion';

export interface VideoMetadata {
  platform: SupportedPlatform;
  videoId: string;
  thumbnailUrl: string;
  embedUrl: string;
  title?: string;
  duration?: number;
}

/**
 * Extract video ID and platform from supported video platforms
 */
export function parseVideoUrl(url: string): { platform: SupportedPlatform; videoId: string } | null {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/
  ];

  // Vimeo patterns
  const vimeoPatterns = [
    /vimeo\.com\/(\d+)/,
    /player\.vimeo\.com\/video\/(\d+)/
  ];

  // Facebook patterns
  const facebookPatterns = [
    /facebook\.com\/.*\/videos\/(\d+)/,
    /fb\.watch\/([a-zA-Z0-9_-]+)/
  ];

  // TikTok patterns
  const tiktokPatterns = [
    /tiktok\.com\/@[^\/]+\/video\/(\d+)/,
    /tiktok\.com\/.*\/video\/(\d+)/
  ];

  // Instagram patterns
  const instagramPatterns = [
    /instagram\.com\/(?:p|reel)\/([a-zA-Z0-9_-]+)/
  ];

  // Twitch patterns
  const twitchPatterns = [
    /twitch\.tv\/videos\/(\d+)/
  ];

  // Dailymotion patterns
  const dailymotionPatterns = [
    /dailymotion\.com\/video\/([a-zA-Z0-9_-]+)/
  ];

  // Check YouTube
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'youtube',
        videoId: match[1]
      };
    }
  }

  // Check Vimeo
  for (const pattern of vimeoPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'vimeo',
        videoId: match[1]
      };
    }
  }

  // Check Facebook
  for (const pattern of facebookPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'facebook',
        videoId: match[1]
      };
    }
  }

  // Check TikTok
  for (const pattern of tiktokPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'tiktok',
        videoId: match[1]
      };
    }
  }

  // Check Instagram
  for (const pattern of instagramPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'instagram',
        videoId: match[1]
      };
    }
  }

  // Check Twitch
  for (const pattern of twitchPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'twitch',
        videoId: match[1]
      };
    }
  }

  // Check Dailymotion
  for (const pattern of dailymotionPatterns) {
    const match = url.match(pattern);
    if (match) {
      return {
        platform: 'dailymotion',
        videoId: match[1]
      };
    }
  }

  return null;
}

/**
 * Generate thumbnail URL based on platform and video ID
 */
export function generateThumbnailUrl(platform: SupportedPlatform, videoId: string): string {
  switch (platform) {
    case 'youtube':
      // Use maxresdefault for best quality, fallback to hqdefault if not available
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    
    case 'vimeo':
      // For Vimeo, we'll need to make an API call to get thumbnail
      // For now, return a placeholder - this will be handled by the API
      return `https://vumbnail.com/${videoId}.jpg`;
    
    case 'facebook':
      // Facebook doesn't provide direct thumbnail URLs
      // Return empty string - will need to be handled by metadata service
      return '';
    
    case 'tiktok':
      // TikTok doesn't provide direct thumbnail URLs
      // Return empty string - will need to be handled by metadata service
      return '';
    
    case 'instagram':
      // Instagram doesn't provide direct thumbnail URLs
      // Return empty string - will need to be handled by metadata service
      return '';
    
    case 'twitch':
      // Twitch video thumbnails follow a pattern
      return `https://static-cdn.jtvnw.net/cf_vods/d2nvs31859zcd8/twitchvod${videoId}/thumb/thumb-320x180.jpg`;
    
    case 'dailymotion':
      // Dailymotion thumbnails follow a pattern
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    
    default:
      return '';
  }
}

/**
 * Generate embed URL for video player
 */
export function generateEmbedUrl(platform: SupportedPlatform, videoId: string): string {
  switch (platform) {
    case 'youtube':
      return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
    
    case 'vimeo':
      return `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`;
    
    case 'facebook':
      // Facebook embeds require different approach
      return `https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/video.php?v=${videoId}`;
    
    case 'tiktok':
      // TikTok embeds are not directly supported in this way
      // Return empty string - would need special iframe handling
      return '';
    
    case 'instagram':
      // Instagram embeds are not directly supported in this way
      // Return empty string - would need special iframe handling
      return '';
    
    case 'twitch':
      return `https://player.twitch.tv/?video=${videoId}&parent=localhost&parent=lightshunt.com`;
    
    case 'dailymotion':
      return `https://www.dailymotion.com/embed/video/${videoId}`;
    
    default:
      return '';
  }
}

/**
 * Validate if a URL is a supported video platform
 */
export function isValidVideoUrl(url: string): boolean {
  return parseVideoUrl(url) !== null;
}

/**
 * Get video metadata from URL (client-side basic extraction)
 */
export function getBasicVideoMetadata(url: string): VideoMetadata | null {
  const parsed = parseVideoUrl(url);
  if (!parsed) return null;

  const { platform, videoId } = parsed;
  
  return {
    platform,
    videoId,
    thumbnailUrl: generateThumbnailUrl(platform, videoId),
    embedUrl: generateEmbedUrl(platform, videoId)
  };
}

/**
 * Format video duration from seconds to MM:SS or HH:MM:SS
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}

/**
 * Extract video title from YouTube/Vimeo page title (basic)
 */
export function extractTitleFromUrl(url: string): string {
  const parsed = parseVideoUrl(url);
  if (!parsed) return 'Untitled Video';
  
  // This is a fallback - actual title should be fetched from API
  return `${parsed.platform.charAt(0).toUpperCase() + parsed.platform.slice(1)} Video`;
}

/**
 * Check if thumbnail exists (for YouTube maxresdefault fallback)
 */
export async function checkThumbnailExists(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Get YouTube thumbnail with fallback to lower quality
 */
export async function getYouTubeThumbnail(videoId: string): Promise<string> {
  const maxResUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  
  if (await checkThumbnailExists(maxResUrl)) {
    return maxResUrl;
  }
  
  // Fallback to high quality default
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

/**
 * Video URL validation with detailed error messages
 */
export function validateVideoUrl(url: string): { isValid: boolean; error?: string } {
  if (!url.trim()) {
    return { isValid: false, error: 'Video URL is required' };
  }

  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return { isValid: false, error: 'URL must start with http:// or https://' };
  }

  const parsed = parseVideoUrl(url);
  if (!parsed) {
    return { 
      isValid: false, 
      error: 'URL must be a valid YouTube or Vimeo link' 
    };
  }

  return { isValid: true };
}